/** @jsxImportSource davaux/client */
import { reactive } from 'davaux'
import { createSignal } from 'davaux/client'
import { Pill } from './Pill.js'
import type { UiRadius, UiSize } from './types.js'
import { pickStyleProps } from './types.js'

export interface MultiSelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface MultiSelectProps {
  /** Option list — accepts strings or { value, label, disabled } objects. */
  data?: (string | MultiSelectOption)[]
  /** Currently selected values. */
  value?: string[]
  /** Placeholder shown in the search field when nothing is selected. */
  placeholder?: string
  label?: string
  description?: string
  error?: string | boolean
  disabled?: boolean
  /** Maximum number of values that can be selected. */
  maxValues?: number
  /** Message shown when no options match. */
  nothingFound?: string
  id?: string
  name?: string
  required?: boolean
  withAsterisk?: boolean
  size?: UiSize
  radius?: UiRadius
  class?: string
  [key: string]: unknown
}

const CHEVRON =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="0.75em" height="0.75em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6l4 4 4-4"/></svg>'

function MultiSelect({
  data = [],
  value: initialValue = [],
  placeholder = 'Select values…',
  label,
  description,
  error,
  disabled = false,
  maxValues,
  nothingFound = 'Nothing found',
  id,
  name,
  required = false,
  withAsterisk = false,
  size,
  radius: r,
  class: className = '',
  ...rest
}: MultiSelectProps) {
  const [open, setOpen] = createSignal(false)
  const [values, setValues] = createSignal<string[]>(initialValue)
  const [search, setSearch] = createSignal('')
  const [activeValue, setActiveValue] = createSignal<string | null>(null)

  let inputEl: HTMLInputElement | null = null

  const options: MultiSelectOption[] = data.map((d) =>
    typeof d === 'string' ? { value: d, label: d } : d,
  )

  const selectedOptions = () => values().map((v) => options.find((o) => o.value === v)).filter(Boolean) as MultiSelectOption[]

  const filtered = () => {
    const q = search().toLowerCase()
    const selected = new Set(values())
    return options.filter(
      (o) => !selected.has(o.value) && (!q || o.label.toLowerCase().includes(q)),
    )
  }
  const navigable = () => filtered().filter((o) => !o.disabled)
  const atMax = () => maxValues !== undefined && values().length >= maxValues

  const hasAsterisk = required || withAsterisk
  const hasError = !!error

  const cssVars: Record<string, string> = {}
  if (size !== undefined) {
    cssVars['--dv-input-fz'] = `var(--dv-input-fz-${size})`
    cssVars['--dv-input-size'] = `var(--dv-input-size-${size})`
  }
  if (r !== undefined) {
    const radii: Record<string, string> = {
      xs: 'var(--dv-radius-xs)',
      sm: 'var(--dv-radius-sm)',
      md: 'var(--dv-radius-md)',
      lg: 'var(--dv-radius-lg)',
      xl: 'var(--dv-radius-xl)',
    }
    cssVars['--dv-input-radius'] = radii[r] ?? r
  }
  const { ownRest } = pickStyleProps(rest as Record<string, unknown>)
  const style = Object.keys(cssVars).length
    ? Object.entries(cssVars).map(([k, v]) => `${k}:${v}`).join(';')
    : undefined

  function openDropdown() {
    if (disabled || atMax()) return
    setActiveValue(null)
    setOpen(true)
  }

  function clearSearch() {
    setSearch('')
    if (inputEl) inputEl.value = ''
  }

  function closeDropdown() {
    setOpen(false)
    clearSearch()
    setActiveValue(null)
  }

  function addValue(v: string) {
    if (atMax()) return
    setValues([...values(), v])
    clearSearch()
    setActiveValue(null)
    if (atMax()) closeDropdown()
  }

  function removeValue(v: string) {
    setValues(values().filter((x) => x !== v))
  }

  function handleInput(e: Event) {
    const v = (e.target as HTMLInputElement).value
    setSearch(v)
    setActiveValue(null)
    if (!open()) setOpen(true)
  }

  function handleWrapperClick(e: Event) {
    if (disabled) return
    if ((e.target as HTMLElement).closest('.dv-pill-remove')) return
    ;(e.currentTarget as HTMLElement).querySelector<HTMLInputElement>('.dv-multi-select-search')?.focus()
    if (!open()) openDropdown()
  }

  function handleBlur() {
    setTimeout(() => closeDropdown(), 150)
  }

  function handleKeyDown(e: KeyboardEvent) {
    const nav = navigable()
    const currentIdx = nav.findIndex((o) => o.value === activeValue())

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!open()) { setOpen(true); return }
      const next = nav[currentIdx + 1]
      if (next) setActiveValue(next.value)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      const prev = nav[currentIdx - 1]
      if (prev) setActiveValue(prev.value)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const opt = nav.find((o) => o.value === activeValue())
      if (opt) addValue(opt.value)
    } else if (e.key === 'Backspace') {
      if (search() === '' && values().length > 0) {
        removeValue(values()[values().length - 1])
      }
    } else if (e.key === 'Escape') {
      closeDropdown()
    }
  }

  return (
    <div
      class={`dv-input-wrapper dv-multi-select${className ? ` ${className}` : ''}`}
      style={style}
      data-disabled={disabled || undefined}
      data-error={hasError || undefined}
      data-opened={() => open() || undefined}
      {...ownRest}
    >
      {label && (
        <label class='dv-input-label' for={id} data-required={hasAsterisk || undefined}>
          {label}
          {hasAsterisk && (
            <span class='dv-input-asterisk' aria-hidden='true'>
              {' '}
              *
            </span>
          )}
        </label>
      )}
      {description && <p class='dv-input-description'>{description}</p>}
      <div
        class='dv-multi-select-pills'
        onClick={handleWrapperClick}
        onKeyDown={handleKeyDown}
      >
        {() =>
          selectedOptions().map((opt) => (
            <Pill
              size='md'
              withRemoveButton
              onRemove={(e: MouseEvent) => { e.stopPropagation(); removeValue(opt.value) }}
            >
              {opt.label}
            </Pill>
          ))
        }
        {() =>
          !atMax() ? (
            <input
              class='dv-multi-select-search'
              id={id}
              ref={(el: Element) => { inputEl = el as HTMLInputElement }}
              role='combobox'
              aria-expanded={() => open()}
              aria-autocomplete='list'
              aria-haspopup='listbox'
              value={() => search()}
              placeholder={() => (values().length === 0 ? placeholder : '')}
              disabled={disabled}
              onInput={handleInput}
              onFocus={() => { if (!open()) openDropdown() }}
              onBlur={handleBlur}
            />
          ) : null
        }
        <span class='dv-select-chevron' aria-hidden='true' dangerouslySetInnerHTML={{ __html: CHEVRON }} />
      </div>
      <div class='dv-select-dropdown' role='listbox' data-opened={() => open() || undefined}>
        {() => {
          const opts = filtered()
          if (opts.length === 0) return <div class='dv-select-empty'>{nothingFound}</div>
          return opts.map((opt) => (
            <div
              class='dv-select-option'
              role='option'
              aria-selected={false}
              data-disabled={opt.disabled || undefined}
              data-active={opt.value === activeValue() || undefined}
              onMouseDown={(e: Event) => e.preventDefault()}
              onClick={() => { if (!opt.disabled) addValue(opt.value) }}
            >
              {opt.label}
            </div>
          ))
        }}
      </div>
      {name && values().map((v) => <input type='hidden' name={`${name}[]`} value={v} />)}
      {hasError && error !== true && <p class='dv-input-error'>{error as string}</p>}
    </div>
  )
}

export default reactive(MultiSelect)
